import { CircleLoader, LoaderSizes } from '@brandfolder/react';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import { PeoplePageResponse } from '@components/people_tags/customPeopleTagTypes';

import { EmptyState } from './EmptyState';
import { useFetchPeopleHubData } from './hooks/useFetchPeopleHubData';
import { PeopleManagementActionsBar } from './PeopleManagementActionsBar';
import { PeopleManagementProvider, usePeopleManagementContext } from './PeopleManagementContext';
import { PeopleManagementFilter } from './PeopleManagementFilter';
import { PeoplePageHeader } from './PeoplePageHeader';
import { PersonCard } from './PersonCard';

const PeopleTagsFaceCardsWrapper = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(1, 100%);
  justify-content: center;

  @media (min-width: 320px) {
    grid-template-columns: repeat(2, 48%);
  }
  @media (min-width: 744px) {
    grid-template-columns: repeat(2, minmax(166px, 1fr));
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(166px, 1fr));
  }
  @media (min-width: 1320px) {
    grid-template-columns: repeat(4, minmax(194px, 1fr));
  }
  @media (min-width: 1526px) {
    grid-template-columns: repeat(5, minmax(229px, 1fr));
  }
  @media (min-width: 1728px) {
    grid-template-columns: repeat(6, minmax(15%, 1fr));
  }
`;

interface PeopleManagementContentProps {
  initialLoadingState: boolean;
  peopleData: PeoplePageResponse['data'];
  peopleDataLoading: boolean;
  bfProcessed: boolean;
}

interface PeopleManagementContentProps {
  initialLoadingState: boolean;
  peopleData: PeoplePageResponse['data'];
  peopleDataLoading: boolean;
  bfProcessed: boolean;
}

const PeopleManagementContent: FunctionComponent<PeopleManagementContentProps> = ({
  initialLoadingState,
  peopleData,
  peopleDataLoading,
  bfProcessed
}) => {
  const showPageLoader = initialLoadingState || peopleDataLoading;
  const showEmptyState = !peopleData?.length && !peopleDataLoading && !initialLoadingState;

  if (showPageLoader) {
    return <CircleLoader label="people-data" size={LoaderSizes.Large} />;
  }

  if (showEmptyState) {
    return <EmptyState bfProcessed={bfProcessed} />;
  }

  if (!peopleData?.length) {
    return null;
  }

  return (
    <>
    <PeopleTagsFaceCardsWrapper>
      {peopleData.map((faceCard) => (
        <PersonCard key={faceCard.person_id} imageUrl={faceCard.image_url} name={faceCard.face_label} />
      ))}
    </PeopleTagsFaceCardsWrapper>
    </>
  );
};

export const PeopleManagement: FunctionComponent = () => {
  const { updateFilter, state } = usePeopleManagementContext();
  const {
    initialLoadingState,
    peopleData,
    peopleDataLoading,
  } = useFetchPeopleHubData();

  const [counts, setCounts] = useState({
    allCount: 0,
    namedCount: 0,
    unnamedCount: 0
  });

  const filteredData = useMemo(() => {
    if (!peopleData?.data) {
      return [];
    }

    const allData = peopleData.data;

    switch (state.filter) {
      case 'named':
        return allData.filter((person) => person.face_label && person.face_label.trim() !== '');
      case 'unnamed':
        return allData.filter((person) => !person.face_label || person.face_label.trim() === '');
      default:
        return allData;
    }
  }, [peopleData?.data, state.filter]);

  useEffect(() => {
    if (peopleData?.data && !peopleDataLoading) {
      const allData = peopleData.data;
      const named = allData.filter((person) => person.face_label && person.face_label.trim() !== '');
      const unnamed = allData.filter((person) => !person.face_label || person.face_label.trim() === '');

      setCounts({
        allCount: allData.length,
        namedCount: named.length,
        unnamedCount: unnamed.length
      });
    }
  }, [peopleData?.data, peopleDataLoading]);

  const { allCount, namedCount, unnamedCount } = counts;

  return (
    <I18nProviderWrapper>
      <div className="people-management-wrapper">
        {/* Removed nested PeopleManagementProvider */}
        <PeoplePageHeader />
        <PeopleManagementActionsBar />
        <PeopleManagementFilter
          activeFilter={state.filter}
          allCount={allCount}
          namedCount={namedCount}
          onFilterChange={updateFilter}
          unnamedCount={unnamedCount}
        />
        <PeopleManagementContent
          bfProcessed={peopleData?.meta.bf_processed}
          initialLoadingState={initialLoadingState}
          peopleData={filteredData}
          peopleDataLoading={peopleDataLoading}
        />
      </div>
    </I18nProviderWrapper>
  );
};

export default (): JSX.Element => {
  return (
    <PeopleManagementProvider>
      <PeopleManagement />
    </PeopleManagementProvider>
  );
};
